/**
 * CustomerProfileComplete — pagina pubblica /p/:token.
 * Il cliente arricchisce il proprio profilo. La prenotazione NON cambia stato.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicProfileApi } from "@/lib/reservations";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const CHIP = "px-3 py-1 rounded-full border text-sm cursor-pointer select-none";

const ALL_ALLERGIES = ["Glutine", "Lattosio", "Frutta secca", "Crostacei", "Pesce", "Uova", "Soia", "Solfiti"];
const ALL_PREFS = ["Fumatore", "Tavolo interno", "Tavolo esterno", "Bambini", "Cane ammesso", "Vegetariano", "Vegano"];

export default function CustomerProfileComplete() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [birthday, setBirthday] = useState("");
  const [allergies, setAllergies] = useState(new Set());
  const [prefs, setPrefs] = useState(new Set());

  useEffect(() => {
    publicProfileApi.get(token).then((d) => {
      setData(d);
      setEmail(d.email || "");
      setMarketing(!!d.marketing_consent);
      setBirthday(d.birthday || "");
      setAllergies(new Set(d.allergies || []));
      setPrefs(new Set(d.preferences || []));
    }).catch((e) => setErr("Link non valido o scaduto."))
      .finally(() => setLoading(false));
  }, [token]);

  const toggle = (set, setSet, v) => {
    const n = new Set(set);
    n.has(v) ? n.delete(v) : n.add(v);
    setSet(n);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await publicProfileApi.submit(token, {
        email: email || null,
        marketing_consent: marketing,
        birthday: birthday || null,
        allergies: [...allergies],
        preferences: [...prefs],
      });
      setSaved(true);
      toast.success("Profilo salvato. Grazie!");
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Errore, riprova.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-center text-ink-500">Caricamento…</div>;
  if (err) return <div className="p-8 text-center text-rose-600" data-testid="profile-err">{err}</div>;

  if (saved) {
    return (
      <div className="min-h-screen bg-[#f6f2ea] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-3 p-8 rounded-3xl bg-white shadow-lg"
             data-testid="profile-thankyou">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
          <h1 className="text-3xl font-serif">Grazie!</h1>
          <p className="text-ink-600">
            Il tuo profilo è stato aggiornato. La tua prenotazione da <b>Sarabi Beach</b> era
            già confermata: ti aspettiamo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f2ea] py-4">
      <div className="max-w-md mx-auto p-4 space-y-4" data-testid="profile-form">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-800">Sarabi Beach</div>
          <h1 className="text-3xl font-serif">Ciao {data?.name || ""}!</h1>
          <p className="text-sm text-ink-600 mt-1">
            La tua prenotazione è già confermata ✅
            <br />Se ti va, dedica 30 secondi al tuo profilo: potremo offrirti un servizio ancora migliore.
          </p>
        </div>
        <form className="space-y-4" onSubmit={submit}>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-1">Email</div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="w-full h-12 px-3 rounded-2xl bg-white border border-sand-300"
                   data-testid="profile-email" />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-1">Compleanno</div>
            <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)}
                   className="w-full h-12 px-3 rounded-2xl bg-white border border-sand-300 font-mono"
                   data-testid="profile-birthday" />
          </label>
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-1">Allergie</div>
            <div className="flex flex-wrap gap-2">
              {ALL_ALLERGIES.map((a) => (
                <div key={a} onClick={() => toggle(allergies, setAllergies, a)}
                     data-testid={`profile-allergy-${a}`}
                     className={`${CHIP} ${allergies.has(a) ? "bg-rose-100 border-rose-400" : "bg-white border-sand-300"}`}>
                  {a}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-1">Preferenze</div>
            <div className="flex flex-wrap gap-2">
              {ALL_PREFS.map((p) => (
                <div key={p} onClick={() => toggle(prefs, setPrefs, p)}
                     data-testid={`profile-pref-${p}`}
                     className={`${CHIP} ${prefs.has(p) ? "bg-emerald-100 border-emerald-400" : "bg-white border-sand-300"}`}>
                  {p}
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-2 rounded-2xl bg-white border border-sand-300 p-3 cursor-pointer">
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)}
                   className="mt-0.5 w-4 h-4" data-testid="profile-marketing" />
            <span className="text-sm">
              <b>Voglio ricevere offerte e novità</b> · Consenso marketing (revocabile in qualsiasi momento).
            </span>
          </label>
          <button type="submit" disabled={saving}
                  data-testid="profile-submit"
                  className="w-full h-14 rounded-2xl bg-emerald-600 text-white font-serif text-lg active:bg-emerald-700 disabled:opacity-60">
            {saving ? "Salvo…" : "Salva profilo"}
          </button>
          <p className="text-[11px] text-center text-ink-500">
            La compilazione è facoltativa. La prenotazione resta valida in ogni caso.
          </p>
        </form>
      </div>
    </div>
  );
}
